import { LANGUAGE_ROUTES } from '../constants/routes';

export type ApplyProvider = 'fountain' | 'waterfall';
export type ApplyFormSource = { provider: ApplyProvider; url: string };
export type PilotCity = { city: string; slug: string };
export type PilotConfig = Record<string, PilotCity[]>;

const ALLOWED_HOST_SUFFIX = '.goflink.com';
const SLUG = /^[a-z0-9-]{1,100}$/;
const CITY = /^[^<>]{1,40}$/;
const COUNTRY = /^[a-z]{2}$/;

function isAllowedWaterfallUrl(url: URL, nodeEnv: string | undefined): boolean {
  if (url.protocol === 'https:' && url.hostname.endsWith(ALLOWED_HOST_SUFFIX)) return true;
  return nodeEnv === 'development' && url.protocol === 'http:' && url.hostname === 'localhost';
}

function pilotCity(value: unknown): PilotCity | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const { city, slug } = value as { city?: unknown; slug?: unknown };
  if (typeof city !== 'string' || typeof slug !== 'string') return null;
  const trimmed = city.trim();
  if (!CITY.test(trimmed) || !SLUG.test(slug)) return null;
  return { city: trimmed, slug };
}

function pilotCities(value: unknown): PilotCity[] {
  const entries = Array.isArray(value) ? value : [value];
  const seen = new Set<string>();
  const out: PilotCity[] = [];
  for (const entry of entries) {
    const parsed = pilotCity(entry);
    if (parsed && !seen.has(parsed.slug)) {
      seen.add(parsed.slug);
      out.push(parsed);
    }
  }
  return out;
}

// The flag value: per country, the pilot cities that open Waterfall. Anything else means Fountain.
export function parsePilotConfig(value: string | null | undefined): PilotConfig | null {
  if (!value) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;
  const out: PilotConfig = {};
  for (const [country, entry] of Object.entries(parsed)) {
    if (!COUNTRY.test(country)) continue;
    const cities = pilotCities(entry);
    if (cities.length > 0) out[country] = cities;
  }
  return out;
}

export function envApplyFormConfig(
  value: string | undefined = process.env.REACT_APP_APPLY_FORM_CONFIG,
): PilotConfig | null {
  return parsePilotConfig(value);
}

export function waterfallEmbedUrl(
  slug: string,
  base: string | undefined = process.env.REACT_APP_WATERFALL_BASE_URL,
  nodeEnv: string | undefined = process.env.NODE_ENV,
): string | null {
  if (!base || !SLUG.test(slug)) return null;
  let url: URL;
  try {
    url = new URL(`${base.replace(/\/+$/, '')}/apply/${encodeURIComponent(slug)}/embed`);
  } catch {
    return null;
  }
  url.searchParams.set('start', '1');
  return isAllowedWaterfallUrl(url, nodeEnv) ? url.toString() : null;
}

// Only cities whose Waterfall form can be built are offered; without a base URL nobody is asked.
export function pilotCitiesFor(
  config: PilotConfig | null,
  country: string,
  base?: string,
  nodeEnv?: string,
): PilotCity[] {
  return (config?.[country] ?? []).filter((c) => waterfallEmbedUrl(c.slug, base, nodeEnv) !== null);
}

export function cityPickerSource(
  pilots: PilotCity[],
  lang: string,
  base?: string,
  nodeEnv?: string,
): ApplyFormSource | null {
  if (pilots.length === 0) return null;
  const url = waterfallEmbedUrl(pilots[0].slug, base, nodeEnv);
  if (!url || !LANGUAGE_ROUTES.includes(lang)) return null;
  const withPicker = new URL(url);
  withPicker.searchParams.set('city_picker', lang);
  withPicker.searchParams.set('cities', pilots.map((p) => p.slug).join(','));
  return { provider: 'waterfall', url: withPicker.toString() };
}
